import React, { useEffect } from 'react';
import {
    Shuffle,
    Layers,
    PenLine,
    Palette,
    Users,
    Hourglass,
    Compass,
    ListChecks,
    Mic,
    FileText,
    Film,
    BarChart3,
    Megaphone,
    LayoutDashboard,
    Video,
    Eye,
    CheckCircle2,
    XCircle
} from 'lucide-react';

/* Reusable card: icon + heading + subheading, nothing else */
const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

export default function PranayPersonalBrand() {

    useEffect(() => {
        document.title = "Pranay Jham | Zero to One Brand";
        window.scrollTo(0, 0);
    }, []);

    const steps = [
        { n: '01', h: 'You fill the questionnaire', s: 'Your own copy of the doc is linked at the bottom of this page. 20 minutes, answered by you, no AI. You already said yes to this part.' },
        { n: '02', h: 'We research the same day', s: 'Your profile, your niche, and whether your existing audience fits the plan, the thing you raised on the call. Then an exact number in writing.' },
        { n: '03', h: 'Agreement, then 7-day onboarding', s: 'We download your arc, your takes and your experience, and lock positioning and content pillars. A boundary for every video.' },
        { n: '04', h: 'The engine runs', s: 'Minimum one reel a day across Instagram, YouTube Shorts and Facebook. Voice notes in, finished videos out for your approval.' }
    ];

    const wont = [
        { h: 'An AI clone or AI scripts', s: 'You said no AI and we agree: your camera presence is the unfair advantage. Every script is built from your experience, or it does not go out.' },
        { h: 'Views for their own sake', s: 'The followers we chase are people starting or running D2C businesses, not friends and family. In-ICP or it does not count.' },
        { h: 'Help past the booked call', s: 'We fill the top of the funnel and work the DMs. Consulting, closing and delivery stay yours.' }
    ];

    const will = [
        { h: '10,000 followers in 3 months, in writing', s: 'In-ICP followers, in a legally bound contract. Miss it and 100% of your money comes back.' },
        { h: 'An exact number before you commit', s: 'The range holds at Rs 1L to 1.25L because there is no AI clone to build. The final figure comes after we study your profile, exactly as discussed.' },
        { h: 'Monetization built in when ready', s: 'Once the position strengthens: comment-to-DM resource funnels, optimized bio and story flow, and consulting calls. The brand starts paying for itself.' }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Pranay Jham</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Private</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Confidential · Private Proposal
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The zero to one voice<br />for Indian D2C.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-16 font-light">
                        Hormozi teaches 10 to 100. Nobody teaches the messy first phase, bootstrapped, in India.<br className="hidden sm:block" /> You have done it twice with your own money. You record, we run everything else.
                    </p>

                    <div className="max-w-md mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-6 py-8">
                            <div className="text-[10px] sm:text-xs text-white/40 font-bold tracking-widest uppercase mb-3">Investment · Monthly</div>
                            <div className="text-4xl sm:text-5xl font-black text-white tracking-tight">₹1L to ₹1.25L</div>
                            <div className="text-xs sm:text-sm text-white/50 mt-2">per month · 3-month minimum · lower range because no AI clone · exact number in writing after research</div>
                        </div>
                    </div>
                </section>

                {/* THE PROCESS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The process</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Questionnaire today.<br />Number tomorrow.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            The exact order of operations from the call, so nothing locks in before you have the figure in hand.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {steps.map((step) => (
                            <div key={step.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{step.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{step.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{step.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* PAIN POINTS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Where you are right now.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Shuffle} heading="Some things work, no pattern" subheading="your words on the call · without data there is no way to know why a video lands or dies" accent="rose" />
                        <InfoCard icon={Layers} heading="All over the place" subheading="story, tools, comments, advice · no pillars, no boundary, every video a fresh guess" accent="rose" />
                        <InfoCard icon={PenLine} heading="Scripting and hooks are the gap" subheading="recording takes you 15 minutes · finding the right words for the right audience takes everything else" accent="rose" />
                        <InfoCard icon={Palette} heading="No brand consistency yet" subheading="the edits change video to video · nothing on the page says one clear thing" accent="rose" />
                        <InfoCard icon={Users} heading="960 followers, mostly friends" subheading="your estimate: maybe 10% care about D2C · the audience is personal, not positioned" accent="rose" />
                        <InfoCard icon={Hourglass} heading="Two brands already eat your day" subheading="the personal brand only happens if it never competes with running Kikoriko" accent="rose" />
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Our side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we build and run.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                        <InfoCard icon={Compass} heading="Positioning locked first" subheading="the zero to one guy for Indian D2C bootstrappers · the wedge the big names leave open" accent="indigo" />
                        <InfoCard icon={ListChecks} heading="Content pillars, not guesses" subheading="7-day onboarding downloads your arc and your takes · a boundary for every video" accent="indigo" />
                        <InfoCard icon={Mic} heading="Your takes, extracted" subheading="voice notes on WhatsApp plus the right questions · the crux always comes from you" accent="indigo" />
                        <InfoCard icon={FileText} heading="Scripts in your voice" subheading="hooks, structure and words done for you · written to be read off your teleprompter in one take" accent="indigo" />
                        <InfoCard icon={Film} heading="Editing with one identity" subheading="your raw style kept, the look unified · text hooks, subtitles and covers as one system" accent="indigo" />
                        <InfoCard icon={BarChart3} heading="6,000 posts of data behind topics" subheading="what works in one niche gets rebuilt with your D2C take · trends caught the day they land" accent="indigo" />
                        <InfoCard icon={Megaphone} heading="Posted everywhere, daily" subheading="minimum one reel a day on Instagram, YouTube Shorts and Facebook · volume scales when a format wins" accent="indigo" />
                        <InfoCard icon={LayoutDashboard} heading="Dashboard and DM funnels" subheading="the Meta-verified app tracks every video · comment-to-resource auto-DMs you already believe in" accent="indigo" />
                    </div>
                </section>

                {/* YOUR SIDE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">Your side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we need from you.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You said 15 minutes a video and two a day is doable. That is the whole ask.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={ListChecks} heading="20 minutes, yourself" subheading="the questionnaire, no AI · the strategy is built on it" accent="emerald" />
                        <InfoCard icon={Mic} heading="Voice notes when thoughts hit" subheading="open WhatsApp, dump the take · that is the whole ideation ask" accent="emerald" />
                        <InfoCard icon={Video} heading="Record on your setup" subheading="your office, your teleprompter, one or two takes · the camera presence stays yours" accent="emerald" />
                        <InfoCard icon={Eye} heading="Approve before it ships" subheading="review the final cut · take the calls that come out of the DMs" accent="emerald" />
                    </div>
                </section>

                {/* PROMISE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The exchange</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we promise.<br />And what we won't.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        {/* WON'T */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
                            <div className="text-rose-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we won't promise</div>
                            <ul className="space-y-6">
                                {wont.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/50 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* WILL */}
                        <div className="rounded-3xl border border-indigo-500/30 bg-indigo-900/10 p-7 sm:p-9">
                            <div className="text-indigo-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we do promise</div>
                            <ul className="space-y-6">
                                {will.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        The questionnaire is ready below.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        This is your own copy, just open and fill it. Research happens the same day, and we lock the number on tomorrow's call.
                    </p>
                    <a
                        href="https://docs.google.com/document/d/1JVr_W5svz_ymlwaBRIuVmBbbZ947QoKoTFFfwR0kwGE/edit"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        Open your questionnaire
                    </a>
                    <div className="mt-6">
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition text-sm">or write to suresh@socialseo.in</a>
                    </div>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
